/**
 * Thin presentational shops-directory row chrome (subtitle path).
 * Builds `tenant · location · description · place · distance` via buildShopDirectorySubtitleParts.
 * Used by ShopDirectoryCard and hermetic G1/G3 row tests.
 */
import type { ReactNode } from 'react';
import {
  DirectoryEntityRow,
  type DirectoryEntityRowDensity,
  type DirectoryEntityRowLayout,
} from '../../shared/ui/DirectoryEntityRow.js';
import type { DirectoryEntityThumbProps } from '../../shared/ui/DirectoryEntityThumb.js';
import { buildShopDirectorySubtitleParts } from './shopDirectorySubtitle.js';

export type ShopDirectoryRowViewProps = {
  readonly title: string;
  readonly tenantLabel?: string | null;
  readonly location: string;
  readonly description?: string | null;
  readonly placeLabel?: string | null;
  readonly distanceLabel?: string | null;
  readonly thumb?: DirectoryEntityThumbProps;
  readonly trailing?: ReactNode;
  readonly onRowPress?: () => void;
  /** In-app href for Link row press (right-click / modified navigation). */
  readonly rowPressHref?: string;
  readonly rowPressDisabled?: boolean;
  readonly rowPressTestId?: string;
  readonly showChevron?: boolean;
  readonly density?: DirectoryEntityRowDensity;
  /** `inline` = Seznam; `stack` = Karty cell content. */
  readonly layout?: DirectoryEntityRowLayout;
  readonly testId?: string;
  /** Optional dedicated subtitle test id (hermetic G1/G3). */
  readonly subtitleTestId?: string;
};

const STUB_THUMB: DirectoryEntityThumbProps = {
  candidates: [],
  monogram: { id: 0, displayName: 'Shop', code: 'shop' },
};

export function ShopDirectoryRowView({
  title,
  tenantLabel = null,
  location,
  description = null,
  placeLabel = null,
  distanceLabel = null,
  thumb = STUB_THUMB,
  trailing,
  onRowPress,
  rowPressHref,
  rowPressDisabled,
  rowPressTestId,
  showChevron,
  density = 'comfortable',
  layout = 'inline',
  testId = 'shop-directory-row',
  subtitleTestId,
}: ShopDirectoryRowViewProps): JSX.Element {
  const subtitleText = buildShopDirectorySubtitleParts({
    tenantLabel,
    location,
    description,
    placeLabel,
    distanceLabel,
  }).join(' · ');

  const subtitle =
    subtitleTestId !== undefined ? (
      <span data-testid={subtitleTestId}>{subtitleText}</span>
    ) : (
      subtitleText
    );

  return (
    <DirectoryEntityRow
      thumb={thumb}
      title={title}
      subtitle={subtitle}
      onRowPress={onRowPress}
      rowPressHref={rowPressHref}
      rowPressDisabled={rowPressDisabled}
      rowPressTestId={rowPressTestId}
      showChevron={showChevron}
      density={density}
      layout={layout}
      testId={testId}
      trailing={trailing}
    />
  );
}
